import { SupplierStorefrontV2GetResponseType } from "@sellar-io/api-v2/types";
import { ReactElement } from "react";
import { twMerge } from "tailwind-merge";
import { BadgeIndigo } from "../../../../../components/badge/Badge";
import { Card } from "@sellar-io/components";
import { CaseSizeRuleSummary } from "../../../../../components/caseSizeRules/CaseSizeRuleSummary";
import { PriceTierOrderRuleSummaryStorefront } from "../../../../../components/priceTierOrderRuleSummary/PriceTierOrderRuleSummaryStorefront";
import { ContextOrderBasketType } from "../../../../../hooks";
import { findLatestPreOrderAvailableFromDate } from "@sellar-io/common";
import { isPromotionalOrderItem } from "../StorefrontPromotion/isPromotionalOrderItem";

export const StorefrontOrderRulesCard = ({
  className,
  supplierStorefront,
  order,
  orderTotal,
}: {
  className?: string;
  supplierStorefront: SupplierStorefrontV2GetResponseType | undefined;
  order: ContextOrderBasketType | null | undefined;
  orderTotal: number;
}): ReactElement | null => {
  const orderRules = supplierStorefront?.OrderRules?.PriceTierOrderRules ?? [];
  const minimumOrderPrice =
    supplierStorefront?.OrderRules?.minimumOrderPrice ?? 0;
  const caseSizeRuleList = supplierStorefront?.CaseSizeRules.list ?? [];

  const latestAvailablePreOrderFromDate = findLatestPreOrderAvailableFromDate(
    order?.OrderItems?.map((item) => ({
      availableFrom: item.Product.availableFrom,
    })) ?? [],
  );

  const orderContainsPreOrderItem = !!latestAvailablePreOrderFromDate;

  if (!orderRules.length && !minimumOrderPrice && !caseSizeRuleList.length)
    return null;

  const orderItemList = (
    order?.OrderItems?.map((item) => {
      const { containerType, volume, packQuantity, unit } = item.Product;
      return {
        ...item,
        containerType,
        unit,
        volume,
        packQuantity,
      };
    }) ?? []
  ).filter((orderItem) => {
    return !isPromotionalOrderItem(orderItem); // Do not consider promotional order items when processing case size rules
  });

  return (
    <Card className={twMerge("bg-white", "flex-1 p-4", className)}>
      <BadgeIndigo icon={"CLIPBOARD_CHECK_OUTLINE"}>
        Order requirements
      </BadgeIndigo>
      <div className="flex flex-col gap-2">
        <div className={"mt-2"}>
          <CaseSizeRuleSummary
            orderItemList={orderItemList}
            caseSizeRuleList={caseSizeRuleList}
          />
          {!!caseSizeRuleList && !!orderRules.length && (
            <div className="my-3 border border-solid border-zinc-200" />
          )}
          <PriceTierOrderRuleSummaryStorefront
            priceTierOrderRules={orderRules}
            minimumOrderPrice={minimumOrderPrice}
            supplierStorefront={supplierStorefront}
            currency={order?.currency ?? "gbp"}
            orderItemList={orderItemList}
            orderTotal={orderTotal}
          />
        </div>
        {orderContainsPreOrderItem && (
          <div className="text-indigo-800">
            <span className="font-bold">Pre-order</span> products are{" "}
            <span className="font-bold">exempt</span> from minimum order price
            and quantity rules.
          </div>
        )}
      </div>
    </Card>
  );
};
